import { Form as AntForm, Form } from "antd";
import { useAppDispatch } from "core/utils";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Entity } from "../../core/types/fe";
import { showWarningNotification } from "../NotificationAlerts";
import { DefaultFormLayout } from "./layouts/DefaultFormLayout";
import { InlineFormLayout } from "./layouts/InlineFormLayout";
import { TabsFormLayout } from "./layouts/TabsFormLayout";
import type { FormManagerProps } from "./types";
import { FormLayoutType } from "./types";
import type { FormError } from "./types/types";

// Map with the available layouts
const Layouts = {
    [FormLayoutType.DEFAULT]: DefaultFormLayout,
    [FormLayoutType.TABS]: TabsFormLayout,
    [FormLayoutType.INLINE]: InlineFormLayout,
};

export const FormManager = <T extends Entity>(props: FormManagerProps<T>) => {
    useTranslation();
    const { customFormInstance, name, formData, onChange, formLayout, formDefinition, shouldValidate, onValidated } = props;
    const dispatch = useAppDispatch();

    const [form] = AntForm.useForm(customFormInstance);

    // layoutFormData is only updated when formData prop changes (initial load, watched fields, etc.).
    // Since parent forms only call setFormData for structural changes (not on every keystroke),
    // the formData prop only changes for external updates that need to be applied.
    const [layoutFormData, setLayoutFormData] = useState<T>(formData);

    useEffect(() => {
        const currentValues = form.getFieldsValue();
        if (!isEqual(currentValues, formData)) {
            // setFieldsValue merges rather than replaces: fields whose key is absent from
            // formData (e.g. backend omits null properties from the JSON response) would
            // otherwise keep their stale previous value. Clear known fields first so removed
            // values are actually reflected.
            const clearedValues = Object.fromEntries(Object.keys(currentValues).map((key) => [key, undefined]));
            form.setFieldsValue({ ...clearedValues, ...formData });
        }
        // Update layout so visibility/readOnly/disabled rules reflect the new data.
        setLayoutFormData(formData);
    }, [formData, form]);

    const onFormChange = useCallback(
        (value: T) => {
            onChange(value);
        },
        [onChange],
    );

    const [runValidation, setRunValidation] = useState(false);
    useEffect(() => {
        if (!shouldValidate) {
            return;
        }

        setRunValidation(true);
    }, [shouldValidate, setRunValidation]);

    useEffect(() => {
        if (!onValidated || !runValidation) {
            return;
        }

        form.validateFields()
            .then(() => {
                onValidated(true);
            })
            .catch((error: FormError) => {
                onValidated(false);
                dispatch(
                    showWarningNotification({
                        text: `There are ${error.errorFields.length} validation errors:
                        ${error.errorFields.map((field) => `\n ${field.name.join(".")}: ${field.errors.join("; ")}`).join("")}`,
                        autoCloseDisabled: true,
                    }),
                );
            });

        setRunValidation(false);
    }, [dispatch, form, onValidated, runValidation, setRunValidation]);

    // layoutElement is only recreated when structural data changes (layoutFormData),
    // not on every keystroke.
    const layoutElement = useMemo(
        // @ts-ignore
        () => React.createElement(Layouts[formLayout], { formDefinition, formData: layoutFormData, onChange }),
        [formLayout, formDefinition, layoutFormData, onChange],
    );

    // Render
    return (
        <AntForm
            data-name={name}
            initialValues={formData}
            form={form}
            name={name}
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            onValuesChange={(changedValues, values) => onFormChange(values)}
            autoComplete="off"
        >
            {layoutElement}
            {layoutFormData?.id && (
                <Form.Item key={`${layoutFormData.id}-Id`} name="id" hidden>
                    <input type="hidden" value={layoutFormData.id} />
                </Form.Item>
            )}
        </AntForm>
    );
};
